import React from 'react';
import { Shield, Eye, Lock, Users, Database, Globe, AlertTriangle, CheckCircle } from 'lucide-react';

export const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Shield className="h-16 w-16 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Privacy Policy</h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          {/* Introduction */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">1. Introduction</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              At PrivateTools, we believe that privacy is a fundamental human right. This Privacy Policy explains 
              how we handle your data when you use our services. The good news is simple: we don't collect, store, 
              or process any of your personal data on our servers.
            </p>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-green-800 dark:text-green-400 font-semibold mb-2">Our Privacy Promise</h3>
                  <p className="text-green-700 dark:text-green-300 text-sm">
                    Your data never leaves your device. All processing happens locally in your browser, 
                    ensuring complete privacy and security.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Information We Don't Collect */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">2. Information We Don't Collect</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-red-800 dark:text-red-400 font-semibold mb-2">Personal Information</h3>
                    <ul className="text-red-700 dark:text-red-300 text-sm space-y-1">
                      <li>• Names and contact details</li>
                      <li>• Email addresses</li>
                      <li>• Phone numbers</li>
                      <li>• Physical addresses</li>
                      <li>• IP addresses (we don't log them)</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Database className="h-6 w-6 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-red-800 dark:text-red-400 font-semibold mb-2">Usage Data</h3>
                    <ul className="text-red-700 dark:text-red-300 text-sm space-y-1">
                      <li>• Browsing history</li>
                      <li>• Tool usage patterns</li>
                      <li>• File uploads or content</li>
                      <li>• Search queries</li>
                      <li>• Analytics or tracking data</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How Our Services Work */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">3. How Our Services Work</h2>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Lock className="h-8 w-8 text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-blue-800 dark:text-blue-400 font-semibold mb-3">Client-Side Processing</h3>
                  <div className="space-y-3 text-blue-700 dark:text-blue-300">
                    <div className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-sm">
                        <strong>Local Processing:</strong> All data processing occurs entirely within your web browser using JavaScript.
                      </p>
                    </div>
                    <div className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-sm">
                        <strong>No Server Uploads:</strong> Your files, text, and data never leave your device or get transmitted to our servers.
                      </p>
                    </div>
                    <div className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-sm">
                        <strong>Instant Results:</strong> Processing happens in real-time, providing immediate results without any network delays.
                      </p>
                    </div>
                    <div className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-sm">
                        <strong>Offline Capability:</strong> Once loaded, our tools work without an internet connection.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Data Security */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">4. Data Security</h2>
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Shield className="h-6 w-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-green-800 dark:text-green-400 font-semibold mb-2">Security Measures</h3>
                    <ul className="text-green-700 dark:text-green-300 space-y-1 text-sm">
                      <li>• <strong>No Data Storage:</strong> We don't have databases or servers that store your information</li>
                      <li>• <strong>No Network Transmission:</strong> Your data never travels over the internet to our servers</li>
                      <li>• <strong>Browser Security:</strong> We rely on your browser's built-in security features</li>
                      <li>• <strong>HTTPS Only:</strong> Our website uses secure HTTPS connections</li>
                      <li>• <strong>Input Validation:</strong> All user inputs are validated and sanitized</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cookies and Tracking */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">5. Cookies and Tracking</h2>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Eye className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-yellow-800 dark:text-yellow-400 font-semibold mb-2">No Tracking Technologies</h3>
                  <p className="text-yellow-700 dark:text-yellow-300 text-sm mb-3">
                    We do not use any tracking technologies or cookies to monitor your activity:
                  </p>
                  <ul className="text-yellow-700 dark:text-yellow-300 space-y-1 text-sm">
                    <li>• No cookies for tracking or analytics</li>
                    <li>• No Google Analytics or similar services</li>
                    <li>• No social media tracking pixels</li>
                    <li>• No third-party advertising networks</li>
                    <li>• No behavioral profiling or targeting</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Third-Party Services */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">6. Third-Party Services</h2>
            <div className="space-y-4">
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Globe className="h-6 w-6 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-purple-800 dark:text-purple-400 font-semibold mb-2">Limited Third-Party Dependencies</h3>
                    <p className="text-purple-700 dark:text-purple-300 text-sm mb-3">
                      We use a minimal number of third-party services, all of which are privacy-focused:
                    </p>
                    <ul className="text-purple-700 dark:text-purple-300 space-y-1 text-sm">
                      <li>• <strong>CDN Services:</strong> For fast content delivery (no personal data collected)</li>
                      <li>• <strong>Open Source Libraries:</strong> For tool functionality (processed locally)</li>
                      <li>• <strong>No Advertising Networks:</strong> We don't display ads or use ad networks</li>
                      <li>• <strong>No Social Media Integration:</strong> We don't integrate with social platforms</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Children's Privacy */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">7. Children's Privacy</h2>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Users className="h-6 w-6 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-blue-800 dark:text-blue-400 font-semibold mb-2">Protection for All Users</h3>
                  <p className="text-blue-700 dark:text-blue-300 text-sm">
                    Our services are designed to be safe for users of all ages. Since we don't collect any personal 
                    information, we don't knowingly collect information from children under 13. Our privacy-first 
                    approach ensures that all users, including children, are protected.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Your Rights */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">8. Your Privacy Rights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <h3 className="text-green-800 dark:text-green-400 font-semibold mb-2">Since We Don't Collect Data</h3>
                <ul className="text-green-700 dark:text-green-300 space-y-1 text-sm">
                  <li>• No data to access or request</li>
                  <li>• No data to correct or update</li>
                  <li>• No data to delete or erase</li>
                  <li>• No data to restrict or object to</li>
                  <li>• No data to port or transfer</li>
                </ul>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h3 className="text-blue-800 dark:text-blue-400 font-semibold mb-2">What You Can Do</h3>
                <ul className="text-blue-700 dark:text-blue-300 space-y-1 text-sm">
                  <li>• Use our tools with complete confidence</li>
                  <li>• Clear your browser cache anytime</li>
                  <li>• Use private/incognito browsing</li>
                  <li>• Disable JavaScript if desired</li>
                  <li>• Contact us with privacy questions</li>
                </ul>
              </div>
            </div>
          </div>

          {/* International Users */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">9. International Users</h2>
            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Globe className="h-6 w-6 text-indigo-600 dark:text-indigo-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-indigo-800 dark:text-indigo-400 font-semibold mb-2">Global Privacy Standards</h3>
                  <p className="text-indigo-700 dark:text-indigo-300 text-sm">
                    Our privacy-first approach means we comply with privacy regulations worldwide, including GDPR, 
                    CCPA, and other international privacy laws. Since we don't collect or process personal data, 
                    our services are inherently compliant with strict privacy requirements.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Changes to Privacy Policy */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">10. Changes to This Privacy Policy</h2>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                We may update this Privacy Policy from time to time to reflect changes in our practices or for 
                other operational, legal, or regulatory reasons. We will notify users of any material changes 
                by updating the "Last updated" date at the top of this page. Your continued use of our services 
                after such changes constitutes acceptance of the updated Privacy Policy.
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">11. Contact Us</h2>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Users className="h-6 w-6 text-gray-600 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    If you have any questions about this Privacy Policy or our privacy practices, please review 
                    our Terms of Use or contact us through our website. We are committed to transparency and 
                    will respond to your privacy-related inquiries promptly.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-xl p-6 border border-green-200 dark:border-green-800">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">Privacy Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div>
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Shield className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">No Data Collection</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    We don't collect, store, or process any personal information.
                  </p>
                </div>
                <div>
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Lock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Local Processing</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    All data processing happens in your browser, never on our servers.
                  </p>
                </div>
                <div>
                  <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Eye className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">No Tracking</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No cookies, analytics, or tracking technologies are used.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8 mt-12">
            <div className="text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                By using PrivateTools, you acknowledge that you have read and understood this Privacy Policy.
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                © 2025 PrivateTools. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};